'use client';

import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useRouter } from 'next/navigation';
import { Shield } from 'lucide-react';

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from '@/components/ui/input-otp';
import { Spinner } from '@/components/ui/spinner';
import { Logo } from '@/components/ui/logo';
import { Separator } from '@/components/ui/separator';

import { authClient } from '@/lib/auth/client';

const totpSchema = z.object({
  code: z.string().length(6, 'Code must be 6 digits'),
});
type TotpValues = z.infer<typeof totpSchema>;

const backupCodeSchema = z.object({
  backupCode: z.string().min(1, 'This field is required'),
});
type BackupCodeValues = z.infer<typeof backupCodeSchema>;

export function MfaVerify() {
  const router = useRouter();
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [useBackupCode, setUseBackupCode] = useState(false);

  const totpForm = useForm<TotpValues>({
    resolver: zodResolver(totpSchema),
    defaultValues: { code: '' },
  });

  const backupForm = useForm<BackupCodeValues>({
    resolver: zodResolver(backupCodeSchema),
    defaultValues: { backupCode: '' },
  });

  // input-otp fires deferred setTimeout callbacks on unmount that steal focus.
  // Wait 60ms (past its 0/10/50ms timeouts) before focusing the backup input.
  useEffect(() => {
    if (!useBackupCode) return;
    const timer = setTimeout(() => backupForm.setFocus('backupCode'), 60);
    return () => clearTimeout(timer);
  }, [useBackupCode, backupForm]);

  function handleRedirect(role?: string | null) {
    if (role === 'admin') router.push('/admin/dashboard');
    else if (role === 'investor') router.push('/investor/dashboard');
    else router.push('/founder/dashboard');
  }

  async function onTotpSubmit(values: TotpValues) {
    setError(null);
    setLoading(true);
    try {
      const result = await authClient.twoFactor.verifyTotp({
        code: values.code,
      });
      if (result.error) {
        setError('Invalid verification code. Please try again.');
        return;
      }
      // Read role from the fully-established session rather than the
      // verifyTotp response, which may not reliably include user.role.
      const sessionResult = await authClient.getSession();
      const role = (
        sessionResult.data?.user as Record<string, unknown> | undefined
      )?.role as string | undefined;
      handleRedirect(role);
    } catch {
      setError('Invalid verification code. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  async function onBackupSubmit(values: BackupCodeValues) {
    setError(null);
    setLoading(true);
    try {
      // Use fetch directly to call the backup code verification endpoint
      const response = await fetch('/api/auth/two-factor/verify-backup-code', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ code: values.backupCode }),
      });
      if (!response.ok) {
        const body = (await response.json().catch(() => ({}))) as {
          message?: string;
        };
        const msg = (body.message ?? '').toLowerCase();
        if (msg.includes('all') && msg.includes('used')) {
          setError(
            'All recovery codes have been used. Please contact support.'
          );
        } else {
          setError('Invalid recovery code.');
        }
        return;
      }
      // Read role from the fully-established session rather than the raw
      // response body, which may not reliably include user.role.
      const sessionResult = await authClient.getSession();
      const role = (
        sessionResult.data?.user as Record<string, unknown> | undefined
      )?.role as string | undefined;
      handleRedirect(role);
    } catch {
      setError('Invalid recovery code.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="w-full max-w-md mx-auto">
      <div className="flex flex-col items-center gap-6 mb-8">
        <Logo imageClassName="h-10 w-auto" />
        <div className="text-center">
          <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-3">
            <Shield className="w-6 h-6 text-primary" />
          </div>
          <h1 className="text-2xl font-display font-bold text-foreground">
            Two-Factor Verification
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {useBackupCode
              ? 'Enter a recovery code to verify your identity.'
              : 'Enter the 6-digit code from your authenticator app.'}
          </p>
        </div>
      </div>

      <div className="bg-card border border-border rounded-xl p-6 shadow-sm space-y-6">
        {!useBackupCode ? (
          <Form {...totpForm}>
            <form
              onSubmit={totpForm.handleSubmit(onTotpSubmit)}
              className="space-y-6"
            >
              {error && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
              <FormField
                control={totpForm.control}
                name="code"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-center block">
                      Verification Code
                    </FormLabel>
                    <FormControl>
                      <div className="flex justify-center">
                        <InputOTP
                          maxLength={6}
                          value={field.value}
                          onChange={field.onChange}
                          autoFocus
                        >
                          <InputOTPGroup>
                            <InputOTPSlot index={0} />
                            <InputOTPSlot index={1} />
                            <InputOTPSlot index={2} />
                            <InputOTPSlot index={3} />
                            <InputOTPSlot index={4} />
                            <InputOTPSlot index={5} />
                          </InputOTPGroup>
                        </InputOTP>
                      </div>
                    </FormControl>
                    <FormMessage className="text-center" />
                  </FormItem>
                )}
              />
              <Button
                type="submit"
                className="w-full btn-premium text-white"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Spinner className="mr-2" />
                    Verifying...
                  </>
                ) : (
                  'Verify'
                )}
              </Button>
            </form>
          </Form>
        ) : (
          <Form {...backupForm}>
            <form
              onSubmit={backupForm.handleSubmit(onBackupSubmit)}
              className="space-y-4"
            >
              {error && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
              <FormField
                control={backupForm.control}
                name="backupCode"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Recovery Code</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Enter recovery code"
                        autoComplete="off"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <Button
                type="submit"
                className="w-full btn-premium text-white"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Spinner className="mr-2" />
                    Verifying...
                  </>
                ) : (
                  'Verify Recovery Code'
                )}
              </Button>
            </form>
          </Form>
        )}

        <Separator />

        <button
          type="button"
          onClick={() => {
            setUseBackupCode(!useBackupCode);
            setError(null);
          }}
          className="w-full text-sm text-muted-foreground hover:text-primary transition-colors text-center"
        >
          {useBackupCode
            ? '← Use authenticator app instead'
            : 'Use a recovery code instead'}
        </button>
      </div>
    </div>
  );
}
